import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/utils/auth.utils';
import { UserService } from 'app/core/services/user.service';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { LoginResponse } from '../models/auth.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ResetPassword } from 'app/modules/auth/auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _rememberMe: boolean = false;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        if (this._rememberMe)
            localStorage.setItem('accessToken', token);
        else
            sessionStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken') || '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    public getUserRoles(): string[] {
        try{
            const helper = new JwtHelperService();
            const decodedToken = helper.decodeToken(this.accessToken);

            if (decodedToken) {
                const roleClaim = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

                if (roleClaim) {
                    if (Array.isArray(roleClaim))
                        return roleClaim.map(a => a.trim());

                    return roleClaim.split(',').map(a => a.trim());
                }

                return [];
            }

            return [];
        }
        catch(err){
            return [];
        }

    }
    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/users/auth/forgot-password', {email: email});
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(resetData: ResetPassword): Observable<any> {
        return this._httpClient.post('api/auth/users/reset-password', resetData);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string, rememberMe: boolean }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        this._rememberMe = credentials.rememberMe;

        return this._httpClient.post('api/users/login/password', credentials).pipe(
            switchMap((response: LoginResponse) => {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post('api/users/login/token', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: LoginResponse) => {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        sessionStorage.removeItem('accessToken');
        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    public isInRole(roles: string[]): boolean {
        if (roles != undefined && roles.length > 0) {
            try {
                let isInRole = false;
                const userRoles = this.getUserRoles().map(a => a?.toLowerCase());

                roles.forEach(role => {
                    if (userRoles.indexOf(role.toLowerCase().trim()) > -1) {
                        isInRole = true;
                    }
                });

                return isInRole;
            }
            catch(err) {
                console.log(err);
            }

        } else {
            return false;
        }

    }
}
